import { ref, computed, watch, type Ref } from 'vue'
import {
  addOffsets, shallowOffsets, deriveExtrema, deriveCurve, gridExtrema, reshapes,
  timeshiftMinutes, NO_OFFSETS, gridToPoints, mergeGrids, type Extremum, type LevelPoint, type Offsets
} from '@quicktide/shared'
import { useAppStore } from '../store'
import { levelsBetween, extremaBetween, shiftPoints, type Point } from './tide'

const DAY = 86_400_000

/**
 * The skipper's own correction, added to the astronomy.
 *
 * A prediction is what the moon and the sun say; what the wind is doing today is not in it
 * unless a forecast covers the hours in question. So the reader can say "it is standing
 * thirty centimetres higher than this" and have every reading follow — the depth over a
 * shallow, the room under a bridge, when the boat takes the ground.
 *
 * The astronomy itself stays where it is, drawn as it always is: raising it made the screen
 * claim the moon had said something it had not. The raised curve is a line of its own, and
 * it takes the place of the measured and forecast levels -- it is the skipper's own claim
 * about the same thing, the water as it actually stands, and one claim at a time is enough.
 */
export function raise<T extends { level: number }>(items: T[], cm: number): T[] {
  return cm === 0 ? items : items.map(item => ({ ...item, level: item.level + cm }))
}

/**
 * The best account of the water over a span: the skipper's own rise or fall where one is set;
 * else what was measured and forecast where those reach, with the astronomy either side.
 */
export function bestCurve(astro: Point[], observed: Point[], forecast: Point[], surge: number): Point[] {
  if (surge) return raise(astro, surge)
  const actual = [...observed, ...forecast].sort((a, b) => a.time - b.time)
  if (!actual.length) return astro
  const first = actual[0]!.time
  const last = actual[actual.length - 1]!.time
  return [...astro.filter(p => p.time < first), ...actual, ...astro.filter(p => p.time > last)]
}

/**
 * Everything between a station's tide and the place on screen. A shallow or a bridge shifts
 * it in time; a derived location reshapes it; a shallow behind a derived location does both,
 * the offsets adding up along the chain.
 */
export function usePlaceOffsets() {
  const store = useAppStore()
  const offsets = computed<Offsets>(() => {
    const sub = store.sublocation
    const own = store.shallow ?? store.bridge
    const base: Offsets = sub ? { ...sub } : NO_OFFSETS
    return own ? addOffsets(base, shallowOffsets(own.hwOffsetMinutes, own.lwOffsetMinutes)) : base
  })
  const derived = computed(() => store.shallow !== null || store.bridge !== null || store.sublocation !== null)
  /**
   * With height offsets, or a high water that moves differently from the low, the curve has
   * to be rebuilt between the moved extremes. A plain time shift can move the grid as it is,
   * which keeps the real ten-minute detail.
   */
  const rebuilds = computed(() => reshapes(offsets.value))
  const springLag = computed(() => store.station?.springLagDays)
  const offsetMs = computed(() => (rebuilds.value ? 0 : timeshiftMinutes(offsets.value)) * 60_000)
  return { offsets, derived, rebuilds, springLag, offsetMs }
}

/** Take a grid of levels through the offsets, moving or rebuilding it as the place needs */
export function deriveGrid(
  grid: { start: number; intervalMinutes: number; values: (number | null)[] } | undefined,
  from: number, to: number,
  o: { offsets: Offsets; rebuilds: boolean; springLag: number | undefined; offsetMs: number }
): Point[] {
  if (!grid) return []
  if (!o.rebuilds) return shiftPoints(gridToPoints(grid, from - o.offsetMs, to - o.offsetMs), o.offsetMs)
  const ex = deriveExtrema(gridExtrema(gridToPoints(grid)), o.offsets, o.springLag)
  return ex.length < 2 ? [] : deriveCurve(ex, from, to)
}

/**
 * The measured and forecast levels for the chosen place, derived from its station the same
 * way its prediction is, and merged so there is no hole where the measurements stop.
 * None while the skipper has set their own rise or fall, which takes their place.
 */
export function useDerivedActuals(): { observed: Ref<Point[]>; forecast: Ref<Point[]>; lastObserved: Ref<number | null> } {
  const store = useAppStore()
  const { offsets, rebuilds, springLag, offsetMs } = usePlaceOffsets()
  const opts = computed(() => ({ offsets: offsets.value, rebuilds: rebuilds.value, springLag: springLag.value, offsetMs: offsetMs.value }))
  const merged = computed(() => mergeGrids(store.stationActuals?.observed, store.stationActuals?.forecast))
  const all = computed<Point[]>(() => {
    const g = merged.value
    if (!g) return []
    const pts = gridToPoints(g)
    if (!pts.length) return []
    return deriveGrid(g, pts[0]!.time - DAY, pts[pts.length - 1]!.time + DAY, opts.value)
  })
  const lastObserved = computed(() => {
    const g = store.stationActuals?.observed
    if (!g) return null
    const pts = gridToPoints(g)
    if (!pts.length) return null
    return pts[pts.length - 1]!.time + (rebuilds.value ? timeshiftMinutes(offsets.value) * 60_000 : offsetMs.value)
  })
  const observed = computed(() => (store.settings.surge || lastObserved.value === null ? [] : all.value.filter(p => p.time <= lastObserved.value!)))
  const forecast = computed(() => (!store.settings.surge && store.stationActuals?.forecast ? all.value.filter(p => p.time >= (lastObserved.value ?? -Infinity)) : []))
  return { observed, forecast, lastObserved }
}

/**
 * The astronomical curve and the high and low waters for the chosen place over a span,
 * derived from its station. Reloads when the place, the span or the stored data changes.
 */
export function useDerivedSeries(range: () => { from: number; to: number }): {
  points: Ref<Point[]>
  extrema: Ref<Extremum[]>
} {
  const store = useAppStore()
  const { offsets, derived, rebuilds, springLag, offsetMs } = usePlaceOffsets()
  const points = ref<Point[]>([])
  const extrema = ref<Extremum[]>([])

  async function load() {
    const id = store.settings.stationId
    const { from, to } = range()
    if (!id) { points.value = []; extrema.value = []; return }
    const o = offsets.value
    const shifts = [o.hwLater2, o.hwLater8, o.lwLater2, o.lwLater8, 0]
    const widest = { min: Math.min(...shifts) * 60_000, max: Math.max(...shifts) * 60_000 }
    // reach beyond the span so the extremes bracketing it are in hand before they move
    const wide = 12 * 3600_000
    const all = await extremaBetween(id, from - wide - widest.max, to + wide - widest.min)
    const moved = derived.value ? deriveExtrema(all, o, springLag.value) : all

    // a gauge the source publishes no curve for (BSH: Süderoogsand) rebuilds one from its
    // highs and lows, as a derived place always has
    const stationLevels = rebuilds.value ? [] : shiftPoints(await levelsBetween(id, from - offsetMs.value, to - offsetMs.value), offsetMs.value)
    points.value = rebuilds.value || !stationLevels.length
      ? (moved.length < 2 ? [] : deriveCurve(moved, from, to) as LevelPoint[])
      : stationLevels
    extrema.value = moved.filter(e => e.time >= from && e.time <= to)
  }

  watch(
    () => [store.settings.stationId, store.settings.shallowId, store.settings.sublocationId,
      store.settings.bridgeId, range().from, range().to, store.dataVersion],
    load, { immediate: true }
  )
  return { points, extrema }
}
